/**
 * Product image gallery.
 * Desktop: vertical thumbnails + main image with fade transitions.
 * Mobile: inline gallery + fullscreen swipeable lightbox with tap zoom.
 * Syncs with color swatch selection.
 */
class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.desktopContainer = this.querySelector("[data-main-image]");
    this.mobileContainer = this.querySelector("[data-mobile-gallery]");
    this.thumbnails = this.querySelectorAll("[data-thumbnail]");
    this.galleryMain = this.querySelector("[data-gallery-main]");
    this.desktopZoomStage = this.querySelector("[data-desktop-zoom-stage]");
    this.mobileSlides = this.mobileContainer
      ? this.mobileContainer.querySelectorAll("[data-mobile-slide]")
      : [];
    this.mobileLightbox = this.querySelector("[data-mobile-lightbox]");
    this.mobileLightboxTrack = this.querySelector(
      "[data-mobile-lightbox-track]",
    );
    this.mobileLightboxSlides = this.querySelectorAll(
      "[data-mobile-lightbox-slide]",
    );
    this.mobileLightboxClose = this.querySelector(
      "[data-mobile-lightbox-close]",
    );
    this.mobileLightboxProgress = this.querySelector(
      "[data-mobile-lightbox-progress]",
    );

    this.mobileLightboxPanState = {
      slide: null,
      startX: 0,
      startY: 0,
      originX: 0,
      originY: 0,
      moved: false,
      tapStartX: 0,
      tapStartY: 0,
    };

    // Desktop slides only (scoped to desktop container)
    this.desktopSlides = this.desktopContainer
      ? this.desktopContainer.querySelectorAll("[data-image-slide]")
      : [];

    if (this.desktopSlides.length <= 1 && !this.mobileContainer) return;

    this.thumbnails.forEach((thumb) =>
      thumb.addEventListener("click", () => this.goTo(thumb.dataset.target)),
    );

    this.#syncGalleryHeight();
    this.#initDesktopSwipe();
    this.#initDesktopZoom();
    this.#initMobileLightbox();
    this.#initVariantSync();
  }

  /** Set thumbnail column max-height to match the main image height */
  #syncGalleryHeight() {
    if (!this.galleryMain) return;
    const thumbCol = this.querySelector("[data-thumb-col]");
    if (!thumbCol) return;
    const set = () => {
      const h = this.galleryMain.offsetHeight;
      if (h > 0) {
        thumbCol.style.maxHeight = h + "px";
      }
    };
    set();
    window.addEventListener("resize", set);
  }

  get currentIndex() {
    return [...this.desktopSlides].findIndex(
      (s) => parseFloat(s.style.opacity) === 1,
    );
  }

  goTo(imageId) {
    const id = String(imageId);

    this.#resetDesktopZoom();

    // Update desktop slides via inline style (avoids Tailwind class conflicts)
    this.desktopSlides.forEach((slide) => {
      const isTarget = slide.dataset.imageId === id;
      slide.style.opacity = isTarget ? "1" : "0";
      slide.style.pointerEvents = isTarget ? "" : "none";
      slide.classList.toggle("is-active", isTarget);
      if (!isTarget) {
        slide.classList.remove("is-zooming");
      }
    });

    // Update thumbnails
    this.thumbnails.forEach((thumb) => {
      const isActive = thumb.dataset.target === id;
      thumb.classList.toggle("!opacity-100", isActive);
      thumb.classList.toggle("opacity-50", !isActive);
      thumb.setAttribute("aria-selected", isActive);
    });

    // Scroll mobile gallery to matching slide
    if (this.mobileContainer) {
      const mobileSlide = this.mobileContainer.querySelector(
        `[data-image-id="${id}"]`,
      );
      if (mobileSlide) {
        mobileSlide.scrollIntoView({
          behavior: "smooth",
          inline: "start",
          block: "nearest",
        });
      }
    }

    if (this.mobileLightboxTrack) {
      const lightboxSlide = this.mobileLightboxTrack.querySelector(
        `[data-image-id="${id}"]`,
      );
      if (lightboxSlide) {
        lightboxSlide.scrollIntoView({
          behavior: this.#isLightboxOpen() ? "smooth" : "auto",
          inline: "start",
          block: "nearest",
        });
        this.#updateLightboxProgress();
      }
    }
  }

  next() {
    if (this.desktopSlides.length === 0) return;
    const idx = (this.currentIndex + 1) % this.desktopSlides.length;
    this.goTo(this.desktopSlides[idx].dataset.imageId);
  }

  prev() {
    if (this.desktopSlides.length === 0) return;
    const idx =
      (this.currentIndex - 1 + this.desktopSlides.length) %
      this.desktopSlides.length;
    this.goTo(this.desktopSlides[idx].dataset.imageId);
  }

  #initDesktopSwipe() {
    if (!this.desktopZoomStage) return;

    let startX = 0;
    const target = this.desktopZoomStage;

    target.addEventListener(
      "touchstart",
      (e) => {
        if (this.#isDesktop()) return;
        startX = e.touches[0].clientX;
      },
      { passive: true },
    );

    target.addEventListener(
      "touchend",
      (e) => {
        if (this.#isDesktop()) return;
        const diff = startX - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 50) {
          diff > 0 ? this.next() : this.prev();
        }
      },
      { passive: true },
    );
  }

  #initDesktopZoom() {
    if (!this.desktopZoomStage) return;

    this.desktopZoomStage.addEventListener("click", (event) => {
      if (!this.#isDesktop()) return;
      const slide = this.#getActiveDesktopSlide();
      if (!slide) return;

      const isZooming = slide.classList.contains("is-zooming");
      if (isZooming) {
        this.#resetDesktopZoom();
        return;
      }

      const rect = slide.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 100;
      const y = ((event.clientY - rect.top) / rect.height) * 100;
      const clampedX = Math.max(0, Math.min(100, x));
      const clampedY = Math.max(0, Math.min(100, y));

      slide.style.setProperty("--pdp-zoom-x", `${clampedX}%`);
      slide.style.setProperty("--pdp-zoom-y", `${clampedY}%`);
      slide.classList.add("is-zooming");
      this.desktopZoomStage.classList.add("is-zooming");
    });

    this.desktopZoomStage.addEventListener("mousemove", (event) => {
      if (!this.#isDesktop()) return;
      const slide = this.#getActiveDesktopSlide();
      if (!slide) return;
      if (!slide.classList.contains("is-zooming")) return;

      const rect = slide.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 100;
      const y = ((event.clientY - rect.top) / rect.height) * 100;
      const clampedX = Math.max(0, Math.min(100, x));
      const clampedY = Math.max(0, Math.min(100, y));

      slide.style.setProperty("--pdp-zoom-x", `${clampedX}%`);
      slide.style.setProperty("--pdp-zoom-y", `${clampedY}%`);
    });

    this.desktopZoomStage.addEventListener("mouseleave", () => {
      this.#resetDesktopZoom();
    });
  }

  #initMobileLightbox() {
    if (
      !this.mobileSlides.length ||
      !this.mobileLightbox ||
      !this.mobileLightboxTrack
    )
      return;

    this.mobileSlides.forEach((slide) => {
      slide.addEventListener("click", () => {
        if (this.#isDesktop()) return;
        this.#openMobileLightbox(slide.dataset.imageId);
      });
    });

    this.mobileLightboxClose?.addEventListener("click", () => {
      this.#closeMobileLightbox();
    });

    this.mobileLightboxTrack.addEventListener(
      "scroll",
      () => {
        if (!this.#isLightboxOpen()) return;
        this.#updateLightboxProgress();
      },
      { passive: true },
    );

    this.mobileLightboxSlides.forEach((slide) => {
      const image = slide.querySelector(".pdp-mobile-lightbox__image");
      if (!image) return;

      image.style.setProperty("--pdp-lightbox-scale", "1");
      image.style.setProperty("--pdp-lightbox-x", "0px");
      image.style.setProperty("--pdp-lightbox-y", "0px");

      slide.addEventListener(
        "touchstart",
        (e) => this.#onLightboxTouchStart(e, slide, image),
        { passive: true },
      );

      slide.addEventListener(
        "touchmove",
        (e) => this.#onLightboxTouchMove(e, slide, image),
        { passive: false },
      );

      slide.addEventListener(
        "touchend",
        (e) => this.#onLightboxTouchEnd(e, slide, image),
        { passive: false },
      );
    });

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        this.#closeMobileLightbox();
      }
    });
  }

  #openMobileLightbox(imageId) {
    if (!this.mobileLightbox || !this.mobileLightboxTrack) return;

    this.mobileLightbox.classList.add("is-open");
    this.mobileLightbox.setAttribute("aria-hidden", "false");
    document.documentElement.classList.add("pdp-lightbox-open");
    document.body.classList.add("pdp-lightbox-open");

    this.#resetLightboxZoom();

    const slides = [...this.mobileLightboxSlides];
    const targetIndex = Math.max(
      0,
      slides.findIndex((slide) => slide.dataset.imageId === String(imageId)),
    );
    this.mobileLightboxTrack.scrollTo({
      left: targetIndex * this.mobileLightboxTrack.clientWidth,
      behavior: "auto",
    });
    this.#updateLightboxProgress();
  }

  #closeMobileLightbox() {
    if (!this.mobileLightbox || !this.#isLightboxOpen()) return;

    this.#resetLightboxZoom();
    this.mobileLightbox.classList.remove("is-open");
    this.mobileLightbox.setAttribute("aria-hidden", "true");
    document.documentElement.classList.remove("pdp-lightbox-open");
    document.body.classList.remove("pdp-lightbox-open");
  }

  #isLightboxOpen() {
    return this.mobileLightbox?.classList.contains("is-open");
  }

  #onLightboxTouchStart(event, slide, image) {
    if (this.#isDesktop()) return;
    if (event.touches.length !== 1) return;

    const touch = event.touches[0];
    this.mobileLightboxPanState.tapStartX = touch.clientX;
    this.mobileLightboxPanState.tapStartY = touch.clientY;
    this.mobileLightboxPanState.moved = false;

    if (!this.#isLightboxSlideZoomed(slide)) {
      this.mobileLightboxPanState.slide = null;
      return;
    }

    this.mobileLightboxPanState.slide = slide;
    this.mobileLightboxPanState.startX = touch.clientX;
    this.mobileLightboxPanState.startY = touch.clientY;
    this.mobileLightboxPanState.originX = this.#readPx(
      image,
      "--pdp-lightbox-x",
    );
    this.mobileLightboxPanState.originY = this.#readPx(
      image,
      "--pdp-lightbox-y",
    );
  }

  #onLightboxTouchMove(event, slide, image) {
    if (event.touches.length !== 1) return;

    const touch = event.touches[0];
    const tapDx = touch.clientX - this.mobileLightboxPanState.tapStartX;
    const tapDy = touch.clientY - this.mobileLightboxPanState.tapStartY;
    if (Math.abs(tapDx) > 8 || Math.abs(tapDy) > 8) {
      this.mobileLightboxPanState.moved = true;
    }

    if (this.mobileLightboxPanState.slide !== slide) return;
    if (!this.#isLightboxSlideZoomed(slide)) return;

    const dx = touch.clientX - this.mobileLightboxPanState.startX;
    const dy = touch.clientY - this.mobileLightboxPanState.startY;
    const nextX = this.mobileLightboxPanState.originX + dx;
    const nextY = this.mobileLightboxPanState.originY + dy;

    const scale = this.#readNumber(image, "--pdp-lightbox-scale") || 1;
    const maxX = (slide.clientWidth * scale - slide.clientWidth) / 2;
    const maxY = (slide.clientHeight * scale - slide.clientHeight) / 2;

    const clampedX = Math.max(-maxX, Math.min(maxX, nextX));
    const clampedY = Math.max(-maxY, Math.min(maxY, nextY));

    image.style.setProperty("--pdp-lightbox-x", `${clampedX}px`);
    image.style.setProperty("--pdp-lightbox-y", `${clampedY}px`);

    event.preventDefault();
  }

  #onLightboxTouchEnd(event, slide, image) {
    if (this.#isDesktop()) return;

    if (
      !this.mobileLightboxPanState.moved &&
      event.changedTouches.length === 1
    ) {
      const touch = event.changedTouches[0];
      this.#toggleLightboxSlideZoom(slide, image, touch.clientX, touch.clientY);
      event.preventDefault();
    }

    this.mobileLightboxPanState.slide = null;
  }

  #toggleLightboxSlideZoom(slide, image, clientX, clientY) {
    if (!this.#isLightboxSlideZoomed(slide)) {
      this.#resetLightboxZoom();

      const rect = slide.getBoundingClientRect();
      const x = ((clientX - rect.left) / rect.width) * 100;
      const y = ((clientY - rect.top) / rect.height) * 100;
      const clampedX = Math.max(0, Math.min(100, x));
      const clampedY = Math.max(0, Math.min(100, y));

      image.style.setProperty("--pdp-lightbox-origin-x", `${clampedX}%`);
      image.style.setProperty("--pdp-lightbox-origin-y", `${clampedY}%`);
      image.style.setProperty("--pdp-lightbox-scale", "2.2");
      image.style.setProperty("--pdp-lightbox-x", "0px");
      image.style.setProperty("--pdp-lightbox-y", "0px");
      slide.classList.add("is-zoomed");
      this.mobileLightboxTrack.classList.add("is-zoomed");
      return;
    }

    slide.classList.remove("is-zoomed");
    image.style.setProperty("--pdp-lightbox-origin-x", "50%");
    image.style.setProperty("--pdp-lightbox-origin-y", "50%");
    image.style.setProperty("--pdp-lightbox-scale", "1");
    image.style.setProperty("--pdp-lightbox-x", "0px");
    image.style.setProperty("--pdp-lightbox-y", "0px");
    this.mobileLightboxTrack.classList.remove("is-zoomed");
  }

  #isLightboxSlideZoomed(slide) {
    return slide.classList.contains("is-zoomed");
  }

  #resetLightboxZoom() {
    if (!this.mobileLightboxSlides.length) return;

    this.mobileLightboxSlides.forEach((slide) => {
      const image = slide.querySelector(".pdp-mobile-lightbox__image");
      slide.classList.remove("is-zoomed");
      if (!image) return;
      image.style.setProperty("--pdp-lightbox-origin-x", "50%");
      image.style.setProperty("--pdp-lightbox-origin-y", "50%");
      image.style.setProperty("--pdp-lightbox-scale", "1");
      image.style.setProperty("--pdp-lightbox-x", "0px");
      image.style.setProperty("--pdp-lightbox-y", "0px");
    });

    this.mobileLightboxTrack?.classList.remove("is-zoomed");
  }

  #updateLightboxProgress() {
    if (!this.mobileLightboxTrack || !this.mobileLightboxProgress) return;

    const total = Math.max(
      0,
      this.mobileLightboxTrack.scrollWidth -
        this.mobileLightboxTrack.clientWidth,
    );
    const ratio = total > 0 ? this.mobileLightboxTrack.scrollLeft / total : 0;
    const slideCount = this.mobileLightboxSlides.length || 1;
    const thumbWidth = 100 / slideCount;
    const maxOffset = Math.max(0, 100 - thumbWidth);
    const offset = Math.max(0, Math.min(maxOffset, ratio * maxOffset));

    this.mobileLightboxProgress.style.width = `${thumbWidth}%`;
    this.mobileLightboxProgress.style.left = `${offset}%`;
  }

  #getActiveDesktopSlide() {
    return [...this.desktopSlides].find(
      (slide) => parseFloat(slide.style.opacity) === 1,
    );
  }

  #resetDesktopZoom() {
    if (!this.desktopSlides.length) return;
    this.desktopSlides.forEach((slide) => {
      slide.classList.remove("is-zooming");
      slide.style.setProperty("--pdp-zoom-x", "50%");
      slide.style.setProperty("--pdp-zoom-y", "50%");
    });
    this.desktopZoomStage?.classList.remove("is-zooming");
  }

  #isDesktop() {
    return window.matchMedia("(min-width: 1024px)").matches;
  }

  #readNumber(el, cssVariable) {
    return Number.parseFloat(
      getComputedStyle(el).getPropertyValue(cssVariable).trim(),
    );
  }

  #readPx(el, cssVariable) {
    const value = getComputedStyle(el).getPropertyValue(cssVariable).trim();
    return Number.parseFloat(value.replace("px", "")) || 0;
  }

  #initVariantSync() {
    document.querySelectorAll("[data-color-swatch]").forEach((swatch) => {
      swatch.addEventListener("click", () => {
        const imageId = swatch.dataset.imageId;
        if (imageId) {
          this.goTo(imageId);
        }
      });
    });

    const variantsEl = this.querySelector("[data-product-variants]");
    if (!variantsEl) return;

    let variants;
    try {
      variants = JSON.parse(variantsEl.textContent);
    } catch {
      return;
    }

    const select = document.querySelector('product-form select[name="id"]');
    select?.addEventListener("change", (e) => {
      const variant = variants.find((v) => v.id === Number(e.target.value));
      if (variant?.featured_image) {
        this.goTo(variant.featured_image.id);
      }
    });
  }
}

customElements.define("product-gallery", ProductGallery);

export { ProductGallery };
